import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Chess } from "chess.js";
import { api } from "../lib/api";
import Board from "../components/Board";
import { Sounds } from "../lib/sounds";
import { useClickToMove } from "../lib/useClickToMove";
import { toast } from "sonner";
import { Sparkles, ChevronLeft, ChevronRight, Rewind } from "lucide-react";
import { gradeMove } from "../lib/gameReview";
import { EvalBar } from "../components/EvalBar";

// Classification → colour + label + emoji
const CLASS_STYLE = {
  brilliant:  { color: "#C6A15B", label: "Brilliant",  emoji: "👑" },
  great:      { color: "#EAD08A", label: "Great",      emoji: "⭐" },
  solid:      { color: "#5C8755", label: "Solid",      emoji: "🟢" },
  inaccuracy: { color: "#C6A15B", label: "Inaccuracy", emoji: "🟡" },
  mistake:    { color: "#A04A3C", label: "Mistake",    emoji: "🟠" },
  blunder:    { color: "#8B2E24", label: "Blunder",    emoji: "🔴" },
};

// Text-bar visualization for the phase precision meter.
function phaseBar(cp) {
  if (cp == null) return { pct: 0, label: "N/A", bar: "░░░░░░░░░░" };
  if (cp < 25)  return { pct: 100, label: "Masterclass Precision", bar: "██████████" };
  if (cp < 60)  return { pct: 80,  label: "Super Sharp",           bar: "████████░░" };
  if (cp < 120) return { pct: 60,  label: "Creative Play",         bar: "██████░░░░" };
  return                { pct: 30, label: "Wild Setup",            bar: "███░░░░░░░" };
}

// Momentum bar: gold when advantage is with the user, ivory when with opponent.
function MomentumBar({ moves, userColor }) {
  if (!moves?.length) return null;
  const userIsWhite = userColor === "white";
  return (
    <div data-testid="momentum-bar" className="w-full">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
        <span className="text-[10px] uppercase tracking-widest text-[#756F67] font-mono">Momentum</span>
        <div className="flex items-center gap-2 text-[10px] font-mono flex-wrap">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#C6A15B]" /> you</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#EFE8D8]" /> opponent</span>
        </div>
      </div>
      <div className="w-full h-3 flex rounded-sm overflow-hidden border border-[rgba(239,232,216,0.1)]">
        {moves.map((m, i) => {
          const cp = m.cp_after ?? 0; // white-relative
          const clamped = Math.max(-800, Math.min(800, cp)) / 800; // -1..1
          const advantageForUser = userIsWhite ? clamped : -clamped;
          const isUsersFavor = advantageForUser > 0;
          const strength = Math.min(1, Math.abs(advantageForUser));
          return (
            <div
              key={i}
              title={`Move ${Math.floor(m.ply / 2) + 1}${m.ply % 2 === 0 ? "." : "..."} ${m.san}`}
              className="flex-1"
              style={{
                background: isUsersFavor
                  ? `rgba(198, 161, 91, ${0.25 + strength * 0.65})`
                  : `rgba(239, 232, 216, ${0.25 + strength * 0.65})`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function Stars({ n }) {
  const filled = Math.max(1, Math.min(5, Math.round(n)));
  return (
    <span data-testid="star-rating" aria-label={`${filled} out of 5 stars`}>
      {"⭐".repeat(filled)}
      <span className="opacity-30">{"☆".repeat(5 - filled)}</span>
    </span>
  );
}

export default function AnalysisView() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [currentPly, setCurrentPly] = useState(-1);      // -1 = start position
  const [retryState, setRetryState] = useState({});      // { [ply]: {result} }
  const [retryBoard, setRetryBoard] = useState(null);
  const [retryPly, setRetryPly] = useState(null);

  useEffect(() => {
    api.get(`/analyses/${id}`).then(({ data }) => setA(data));
  }, [id]);

  useEffect(() => {
    if (!a?.all_moves?.length || retryBoard) return undefined;
    const last = a.all_moves.length - 1;
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowRight") { e.preventDefault(); setCurrentPly((p) => Math.min(last, p + 1)); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); setCurrentPly((p) => Math.max(-1, p - 1)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [a, retryBoard]);

  // The board FEN to display: if retrying, show the retry board; otherwise
  // navigate the historical position at currentPly.
  const displayFen = useMemo(() => {
    if (retryBoard) return retryBoard.fen();
    if (!a) return "start";
    if (currentPly < 0 || !a.all_moves?.length) {
      try { return a.all_moves?.[0]?.fen_before || new Chess().fen(); }
      catch { return new Chess().fen(); }
    }
    return a.all_moves[Math.min(currentPly, a.all_moves.length - 1)].fen_after;
  }, [a, currentPly, retryBoard]);

  const currentMove = useMemo(() => {
    if (!a?.all_moves?.length || currentPly < 0) return null;
    return a.all_moves[Math.min(currentPly, a.all_moves.length - 1)];
  }, [a, currentPly]);

  const startRetry = (m) => {
    setRetryPly(m.ply);
    setRetryBoard(new Chess(m.fen_before));
  };

  const cancelRetry = () => {
    setRetryBoard(null);
    setRetryPly(null);
  };

  const submitRetryMove = (from, to, piece, isCapture, promotion) => {
    if (retryPly == null) return false;
    const uci = from + to + (promotion || "");
    if (isCapture) Sounds.capture(); else Sounds.move();
    const c = new Chess(retryBoard.fen());
    try { c.move({ from, to, promotion: promotion || "q" }); } catch { return false; }
    setRetryBoard(c);
    const mv = a.all_moves.find((m) => m.ply === retryPly);
    const ply = retryPly;
    gradeMove(mv.fen_before, uci, mv.best_move_uci)
      .then((data) => {
        setRetryState((s) => ({ ...s, [ply]: data }));
        if (data.is_best) { Sounds.correct(); toast.success(`${data.san} — that's the engine's top choice.`); }
        else if (data.best_move_san) { toast(`${data.san} is playable, but the engine preferred ${data.best_move_san}.`); }
        else { toast(`${data.san} played — the engine couldn't find a better reference move.`); }
      })
      .catch(() => toast.error("The engine couldn't grade that move — try again."));
    return true;
  };

  const clickToMove = useClickToMove(
    retryBoard,
    submitRetryMove,
    !!retryBoard && retryPly != null && !retryState[retryPly],
  );

  if (!a) return <div className="p-12 text-center text-[#A8A196]">Loading…</div>;

  const orient = a.color === "white" ? "white" : "black";
  const counts = a.counts || {};
  const flagged = a.flagged_moves || [];

  const opBar = phaseBar(a.phase_cpl?.opening);
  const mgBar = phaseBar(a.phase_cpl?.middlegame);
  const isShortOrMated = (a.total_moves ?? 0) < 25 || (counts.blunder ?? 0) > 0 && a.top_mistakes?.some((m) => m.allowed_mate);
  const egBar = a.phase_cpl?.endgame != null ? phaseBar(a.phase_cpl.endgame) : null;
  const egLabel = a.phase_cpl?.endgame != null
    ? "Endgame"
    : isShortOrMated ? "Closing / Mating attack" : "Endgame";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      {/* ── Celebratory header ────────────────────────── */}
      <div className="card-panel p-6 sm:p-8 mb-8" data-testid="review-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline mb-2 flex items-center gap-2"><Sparkles size={12} className="text-[#C6A15B]" /> Game performance overview</div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#EFE8D8]" data-testid="review-title">
              {a.title || `Your ${a.opening} review`}
            </h1>
            <div className="mt-3 flex items-center gap-3 text-sm">
              <span className="font-mono text-[#C6A15B]">Coach&apos;s rating:</span>
              <span className="text-2xl leading-none"><Stars n={a.star_rating || 4} /></span>
            </div>
          </div>
          <div className="text-xs font-mono text-[#756F67] text-right">
            <div>{a.total_moves} moves</div>
            <div>{a.opening}</div>
          </div>
        </div>

        {/* Move-quality breakdown */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="quality-breakdown">
          {["brilliant", "great", "solid", "inaccuracy", "mistake", "blunder"].map((k) => {
            const style = CLASS_STYLE[k];
            return (
              <div key={k} data-testid={`count-${k}`} className="card-panel !bg-[#14120F] p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-[#756F67] font-mono">
                  {style.emoji} {style.label}
                </div>
                <div className="font-serif text-2xl mt-1" style={{ color: style.color }}>{counts[k] ?? 0}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Coach's Post-Game Debrief ─────────────────── */}
      {(a.notices || []).length > 0 && (
        <div className="card-panel p-4 mb-6 border-l-2 border-[#C6A15B]" data-testid="analysis-notices">
          {a.notices.map((n, i) => (
            <p key={i} className="text-sm text-[#A8A196]" data-testid={`analysis-notice-${i}`}>{n}</p>
          ))}
        </div>
      )}
      <div className="card-panel p-6 mb-8" data-testid="review-debrief">
        <div className="overline mb-3">🔎 Coach&apos;s post-game debrief</div>
        <p className="text-[#EFE8D8] leading-relaxed" data-testid="debrief-text">{a.debrief || a.summary}</p>
        {a.summary && a.debrief && (
          <p className="text-sm text-[#A8A196] mt-3 leading-relaxed" data-testid="coach-summary">{a.summary}</p>
        )}
      </div>

      {/* ── Phase Precision Meter ─────────────────────── */}
      <div className="card-panel p-6 mb-8" data-testid="phase-meter">
        <div className="overline mb-4">📈 Phase precision meter</div>
        <div className="space-y-3 font-mono text-sm">
          <PhaseRow icon="🎬" label="Opening"    bar={opBar} cp={a.phase_cpl?.opening} />
          {a.phase_cpl?.middlegame != null && (
            <PhaseRow icon="⚔️"  label="Middlegame" bar={mgBar} cp={a.phase_cpl.middlegame} />
          )}
          <PhaseRow
            icon={a.phase_cpl?.endgame == null ? "🎯" : "👑"}
            label={egLabel}
            bar={egBar}
            cp={a.phase_cpl?.endgame}
            fallback={a.phase_cpl?.endgame == null ? "N/A — game ended via direct attack" : null}
          />
        </div>
      </div>

      {/* ── Momentum bar ─────────────────────────────── */}
      <div className="card-panel p-6 mb-8" data-testid="momentum-panel">
        <MomentumBar moves={a.all_moves || []} userColor={a.color} />
      </div>

      {/* ── Move-by-move stepper ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <div className="flex items-stretch gap-2 sm:gap-3">
            <EvalBar
              fen={displayFen}
              savedCp={retryBoard ? a.all_moves?.[retryPly]?.cp_before ?? null : currentPly < 0 ? a.all_moves?.[0]?.cp_before ?? 0 : currentMove?.cp_after ?? null}
              orientation={orient}
              live={!retryBoard}
            />
            <div className="flex-1 min-w-0">
              <Board
                position={displayFen}
                boardOrientation={orient}
                arePiecesDraggable={!!retryBoard && !retryState[retryPly]}
                onPieceDrop={retryBoard ? clickToMove.onPieceDrop : undefined}
                onSquareClick={retryBoard ? clickToMove.onSquareClick : undefined}
                customSquareStyles={retryBoard ? clickToMove.squareStyles : {}}
              />
            </div>
          </div>

          {/* Board navigation controls */}
          {!retryBoard && (
            <div className="mt-4 flex items-center justify-center gap-2" data-testid="board-nav">
              <button onClick={() => setCurrentPly(-1)}                    className="btn-ghost !px-3" data-testid="nav-start"><Rewind size={14} /></button>
              <button onClick={() => setCurrentPly((p) => Math.max(-1, p - 1))} className="btn-ghost !px-3" data-testid="nav-prev"><ChevronLeft size={14} /></button>
              <div className="font-mono text-xs text-[#A8A196] w-24 text-center">
                {currentPly < 0 ? "start" : `#${Math.floor(currentPly / 2) + 1}${currentPly % 2 === 0 ? "." : "..."}`}
              </div>
              <button onClick={() => setCurrentPly((p) => Math.min((a.all_moves?.length ?? 1) - 1, p + 1))} className="btn-ghost !px-3" data-testid="nav-next"><ChevronRight size={14} /></button>
            </div>
          )}
        </div>

        <div className="lg:col-span-5 space-y-4">
          {/* Retry drawer */}
          {retryBoard && retryPly != null ? (
            <div className="card-panel p-5" data-testid="retry-panel">
              <div className="overline mb-2">Retry from move #{Math.floor(retryPly / 2) + 1}</div>
              <p className="text-xs text-[#A8A196] mb-3">Play a move; the engine grades it against its top choice.</p>
              {retryState[retryPly] ? (
                <div className="text-sm font-mono text-[#EFE8D8]" data-testid="retry-result">
                  {retryState[retryPly].is_best
                    ? <span className="text-[#5C8755]">✓ Best move!</span>
                    : <span className="text-[#A04A3C]">Engine preferred {retryState[retryPly].best_move_san}.</span>}
                  <button onClick={() => { setRetryBoard(new Chess(a.all_moves.find(m => m.ply === retryPly).fen_before)); setRetryState(s => { const n = {...s}; delete n[retryPly]; return n; }); }} className="btn-ghost mt-3 w-full">Try again</button>
                  <button onClick={cancelRetry} className="btn-ghost mt-2 w-full">Back to review</button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-[#756F67]">Click or drag a piece to move.</p>
                  <button onClick={cancelRetry} className="btn-ghost mt-3 w-full text-sm">Cancel retry</button>
                </>
              )}
            </div>
          ) : currentMove ? (
            <div className="card-panel p-5" data-testid="current-move-info">
              <div className="overline mb-2">
                Move #{Math.floor(currentMove.ply / 2) + 1}{currentMove.ply % 2 === 0 ? "." : "..."}
              </div>
              <div className="font-serif text-3xl text-[#EFE8D8]" data-testid="current-move-san">{currentMove.san}</div>
              {currentMove.is_user_move && currentMove.classification && (
                <>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-lg">{CLASS_STYLE[currentMove.classification]?.emoji}</span>
                    <span className="font-mono text-sm" style={{ color: CLASS_STYLE[currentMove.classification]?.color }}>
                      {CLASS_STYLE[currentMove.classification]?.label}
                    </span>
                    {!currentMove.allowed_mate && (
                      <span className="ml-auto font-mono text-xs text-[#756F67]">
                        {currentMove.cpl}cp lost
                      </span>
                    )}
                  </div>
                  {currentMove.best_move_san && currentMove.classification !== "brilliant" && currentMove.classification !== "great" && (
                    <div className="mt-4">
                      <div className="text-[10px] uppercase tracking-widest text-[#756F67] font-mono">Engine preferred</div>
                      <div className="font-serif text-xl text-[#C6A15B] mt-1">{currentMove.best_move_san}</div>
                      {currentMove.best_line_san?.length > 0 && (
                        <div className="font-mono text-xs text-[#C6A15B]/70 mt-2">
                          {currentMove.best_line_san.slice(0, 5).join(" ")}
                        </div>
                      )}
                    </div>
                  )}
                  {(currentMove.classification === "inaccuracy" || currentMove.classification === "mistake" || currentMove.classification === "blunder") && (
                    <button
                      onClick={() => startRetry(currentMove)}
                      data-testid={`retry-btn-${currentMove.ply}`}
                      className="btn-brass w-full justify-center mt-4"
                    >
                      Retry from this move
                    </button>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="card-panel p-5">
              <div className="overline mb-2">Ready to browse</div>
              <p className="text-sm text-[#A8A196]">Use ← / → to step through every move. Any flagged mistake gets a &quot;Retry from this move&quot; button.</p>
            </div>
          )}

          {/* Flagged moves quick-list (retry from ANY) */}
          {flagged.length > 0 && !retryBoard && (
            <div className="card-panel p-5" data-testid="flagged-list">
              <div className="overline mb-3">All flagged moments — {flagged.length}</div>
              <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
                {flagged.map((m) => {
                  const style = CLASS_STYLE[m.classification];
                  const num = Math.floor(m.ply / 2) + 1;
                  return (
                    <button
                      key={m.ply}
                      data-testid={`flagged-${m.ply}`}
                      onClick={() => setCurrentPly(m.ply)}
                      className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-sm hover:bg-[#25211B] transition-colors"
                    >
                      <span className="text-sm">{style?.emoji}</span>
                      <span className="font-mono text-xs text-[#A8A196] w-10">#{num}{m.ply % 2 === 0 ? "." : "..."}</span>
                      <span className="font-serif text-base text-[#EFE8D8] flex-1">{m.san}</span>
                      <span className="font-mono text-xs" style={{ color: style?.color }}>
                        {m.allowed_mate ? "mate" : `${m.cpl}cp`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Coach's notes for top mistakes ──────────── */}
      {a.mistake_notes?.length > 0 && (
        <div className="mt-8">
          <div className="overline mb-3">Coach&apos;s notes</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {a.mistake_notes.map((note, i) => (
              <div key={i} data-testid={`coach-note-${i}`} className="card-panel p-4">
                <p className="text-sm text-[#EFE8D8] leading-snug">{note}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PhaseRow({ icon, label, bar, cp, fallback }) {
  if (fallback) {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        <span className="w-6 flex-shrink-0">{icon}</span>
        <span className="w-28 sm:w-32 text-[#A8A196] flex-shrink-0">{label}</span>
        <span className="text-[#756F67] flex-1 min-w-0">{fallback}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
      <span className="w-6 flex-shrink-0">{icon}</span>
      <span className="w-24 sm:w-32 text-[#A8A196] flex-shrink-0">{label}</span>
      <span className="tracking-widest text-[#C6A15B] whitespace-nowrap text-xs sm:text-sm flex-shrink-0 order-3 sm:order-none w-full sm:w-auto">{bar.bar}</span>
      <span className="text-[#EFE8D8] flex-1 min-w-0 text-xs sm:text-sm">
        {bar.pct}% <span className="text-[#756F67]">{bar.label}</span>
      </span>
      {cp != null && <span className="text-[#756F67] text-xs flex-shrink-0">{cp}cp</span>}
    </div>
  );
}
